/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AdminEnrolmentRowFilter,
  AdminEnrolmentTag,
  MAX_PEOPLE,
  downloadCsv,
  matchesRowFilter,
  peopleCsvTemplate,
  readPeopleCsv,
  rowTags,
  skippedReasonKey,
  skippedRowsCsv,
  summarise,
} from '@app/app/shared/helpers/admin-enrolment.helper';
import { FileUploadHelper } from '@app/app/shared/helpers/file-upload-helper';
import { AdminEnrolment, AdminEnrolmentPerson, AdminEnrolmentResult } from '@app/app/shared/models/playlist/admin-enrolment.model';
import { ADMIN_ENROLMENTS_DATA_SERVICE } from '@app/app/shared/services/admin-enrolments/admin-enrolments-data.service';
import { PtlV3DialogShellComponent } from '@app/app/shared/styleguide/components/dialog-shell/ptl-v3-dialog-shell.component';
import { UploadFileRejection } from '@app/app/shared/styleguide/components/upload-file-area/upload-file-area.component';
import { StyleguideModule } from '@app/app/shared/styleguide/styleguide.module';
import { LucideCircleCheck, LucideDownload, LucideTriangleAlert } from '@lucide/angular';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { EnrolDialogData } from '../enrol-person-dialog/enrol-person-dialog.component';

export type EnrolCsvStep = 'UPLOAD' | 'REVIEW' | 'DONE';

/** One row of the review table: the API's result, with the names the file gave. */
export interface EnrolCsvRow {
  result: AdminEnrolmentResult;
  name: string;
  externalId: string;
  tags: AdminEnrolmentTag[];
  reasonKey: string | null;
}

const ROW_FILTERS: AdminEnrolmentRowFilter[] = ['ALL', 'ENROL', 'NEEDS_A_LOOK', 'SKIPPED'];

const ROW_FILTER_KEYS: Record<AdminEnrolmentRowFilter, string> = {
  ALL: 'translations.adminEnrolment.csv.filter.all',
  ENROL: 'translations.adminEnrolment.csv.filter.enrol',
  NEEDS_A_LOOK: 'translations.adminEnrolment.csv.filter.needsALook',
  SKIPPED: 'translations.adminEnrolment.csv.filter.skipped',
};

/**
 * Enrols people from a CSV file: the file is previewed first, so the admin sees what enrolling them does before anyone is
 * enrolled. Closes with whether anyone was enrolled.
 */
@Component({
  selector: 'ptl-v3-enrol-csv-dialog',
  templateUrl: './enrol-csv-dialog.component.html',
  styleUrls: ['../admin-enrolment-dialog.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, StyleguideModule, PtlV3DialogShellComponent],
})
export class EnrolCsvDialogComponent {
  protected readonly data = inject<EnrolDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<EnrolCsvDialogComponent, boolean>);
  private readonly service = inject(ADMIN_ENROLMENTS_DATA_SERVICE);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly LucideCircleCheck = LucideCircleCheck;
  protected readonly LucideDownload = LucideDownload;
  protected readonly LucideTriangleAlert = LucideTriangleAlert;
  protected readonly maxPeople = MAX_PEOPLE;
  protected readonly rowFilters = ROW_FILTERS;
  protected readonly columns = [
    { name: 'email', neededKey: 'translations.adminEnrolment.csv.column.yes', noteKey: 'translations.adminEnrolment.csv.column.email' },
    {
      name: 'first_name',
      neededKey: 'translations.adminEnrolment.csv.column.newPeople',
      noteKey: 'translations.adminEnrolment.csv.column.name',
    },
    {
      name: 'last_name',
      neededKey: 'translations.adminEnrolment.csv.column.newPeople',
      noteKey: 'translations.adminEnrolment.csv.column.name',
    },
    {
      name: 'external_id',
      neededKey: 'translations.adminEnrolment.csv.column.no',
      noteKey: 'translations.adminEnrolment.csv.column.externalId',
    },
  ];

  readonly step = signal<EnrolCsvStep>('UPLOAD');
  readonly file = signal<File | null>(null);
  readonly busy = signal(false);
  readonly failure = signal<string | null>(null);
  readonly notify = signal(false);
  readonly rowFilter = signal<AdminEnrolmentRowFilter>('ALL');

  /** The preview while reviewing, and what enrolling did once done. */
  readonly enrolment = signal<AdminEnrolment | null>(null);
  private readonly filePeople = signal(new Map<string, AdminEnrolmentPerson>());
  private enrolledAnyone = false;

  readonly summary = computed(() => {
    const enrolment = this.enrolment();
    return enrolment ? summarise(enrolment) : null;
  });

  readonly rows = computed<EnrolCsvRow[]>(() => {
    const people = this.filePeople();
    return (this.enrolment()?.people ?? []).map((result) => {
      const known = people.get((result.email ?? '').toLowerCase());
      return {
        result,
        name: `${known?.firstName ?? ''} ${known?.lastName ?? ''}`.trim(),
        externalId: known?.externalId ?? '',
        tags: rowTags(result),
        reasonKey: skippedReasonKey(result),
      };
    });
  });

  readonly visibleRows = computed(() => this.rows().filter((row) => matchesRowFilter(row.result, this.rowFilter())));

  readonly filterCounts = computed(() =>
    Object.fromEntries(
      ROW_FILTERS.map((rowFilter) => [rowFilter, this.rows().filter((row) => matchesRowFilter(row.result, rowFilter)).length]),
    ),
  );

  rowFilterKey(rowFilter: AdminEnrolmentRowFilter): string {
    return ROW_FILTER_KEYS[rowFilter];
  }

  close(): void {
    this.dialogRef.close(this.enrolledAnyone);
  }

  downloadTemplate(): void {
    downloadCsv(peopleCsvTemplate(), 'enrolment_template.csv');
  }

  onFilesRejected(reason: UploadFileRejection): void {
    this.failure.set(
      this.transloco.translate(
        reason === 'WRONG_TYPE' ? 'translations.adminEnrolment.csv.error.fileType' : 'translations.uploadFileCountLimitError',
      ),
    );
  }

  async onFileSelected(file: File): Promise<void> {
    if (!FileUploadHelper.isValidCsvFile(file)) {
      this.onFilesRejected('WRONG_TYPE');
      return;
    }
    this.failure.set(null);
    this.file.set(file);
    this.filePeople.set(readPeopleCsv(await file.text()));
    this.busy.set(true);
    this.service
      .importCsv(this.data.playlistUid, file, { preview: true, notify: false })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ isSuccess, value, error }) => {
        this.busy.set(false);
        if (!isSuccess || !value) {
          this.failure.set(error || this.transloco.translate('translations.adminEnrolment.csv.error.read'));
          return;
        }
        if (!value.people.length) {
          this.failure.set(this.transloco.translate('translations.adminEnrolment.csv.error.empty'));
          return;
        }
        this.enrolment.set(value);
        this.rowFilter.set('ALL');
        this.step.set('REVIEW');
      });
  }

  back(): void {
    this.reset();
  }

  enrol(): void {
    const file = this.file();
    if (!file || !this.summary()?.enrolled || this.busy()) {
      return;
    }
    this.busy.set(true);
    this.failure.set(null);
    this.service
      .importCsv(this.data.playlistUid, file, { preview: false, notify: this.notify() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ isSuccess, value, error }) => {
        this.busy.set(false);
        if (!isSuccess || !value) {
          this.failure.set(error || this.transloco.translate('translations.adminEnrolment.error.enrol'));
          return;
        }
        this.enrolledAnyone = this.enrolledAnyone || value.people.some((person) => person.outcome === 'ENROLLED');
        this.enrolment.set(value);
        this.step.set('DONE');
      });
  }

  downloadSkipped(): void {
    const enrolment = this.enrolment();
    if (!enrolment) {
      return;
    }
    const csv = skippedRowsCsv(enrolment.people, this.filePeople(), (key) => this.transloco.translate(key));
    downloadCsv(csv, 'enrolment_skipped_rows.csv');
  }

  uploadAnother(): void {
    this.reset();
  }

  private reset(): void {
    this.step.set('UPLOAD');
    this.file.set(null);
    this.enrolment.set(null);
    this.filePeople.set(new Map());
    this.failure.set(null);
    this.notify.set(false);
  }
}
